import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { Select } from 'antd';
import FormItem from '../common/FormItem/FormItem';
import styles from './AddShipmentModal.module.scss';

const { Option } = Select;

// Available products with device inventory
const availableProducts = [
  { 
    name: 'KrossKast Player 8', 
    sku: 'KKP-8', 
    price: 299.99,
    availableDevices: [
      { serialNumber: 'KK8-001', customId: 'CUST-001' },
      { serialNumber: 'KK8-002', customId: 'CUST-002' },
      { serialNumber: 'KK8-003', customId: 'CUST-003' }
    ]
  },
  { 
    name: 'KrossKast Player 11', 
    sku: 'KKP-11', 
    price: 399.99,
    availableDevices: [
      { serialNumber: 'KK11-001', customId: 'CUST-004' },
      { serialNumber: 'KK11-002', customId: 'CUST-005' }
    ]
  },
  { 
    name: 'Accessory Cable', 
    sku: 'AC-001', 
    price: 29.99,
    availableDevices: [
      { serialNumber: 'AC-001', customId: 'CABLE-001' },
      { serialNumber: 'AC-002', customId: 'CABLE-002' }
    ]
  },
];

interface AddShipmentModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (newOrder: NewOrderData) => void;
}

export interface OrderItem {
  productName: string;
  sku: string;
  quantity: number;
  price: number;
  serialNumber?: string;
  customId?: string;
}

export interface NewOrderData {
  name: string;
  orderNumber: string;
  orderDate: string;
  recipientPhone: string;
  recipientAddress1: string;
  recipientAddress2: string;
  recipientCity: string;
  recipientState: string;
  recipientZip: string;
  recipientCountry: string;
  weight: string;
  length: string;
  width: string;
  height: string;
  notes: string;
  items: OrderItem[]; // Products in this order
}

const AddShipmentModal: React.FC<AddShipmentModalProps> = ({ show, onHide, onSuccess }) => {
  console.log('Available products:', availableProducts);
  const [formData, setFormData] = useState<NewOrderData>({
    name: '',
    orderNumber: '',
    orderDate: new Date().toLocaleDateString('en-GB'), // DD/MM/YYYY format
    recipientPhone: '',
    recipientAddress1: '',
    recipientAddress2: '',
    recipientCity: '',
    recipientState: '',
    recipientZip: '',
    recipientCountry: 'US',
    weight: '1.0',
    length: '10',
    width: '10',
    height: '10',
    notes: '',
    items: [],
  });

  const [error, setError] = useState<string | null>(null);
  const [currentItem, setCurrentItem] = useState<OrderItem>({
    productName: '',
    sku: '',
    quantity: 1,
    price: 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentItem({
      ...currentItem,
      [name]: name === 'quantity' || name === 'price' ? parseFloat(value) || 0 : value,
    });
  };

  const handleProductSelect = (value: string) => {
    console.log('Product selected:', value);
    const selectedProduct = availableProducts.find(p => p.name === value);
    if (selectedProduct && selectedProduct.availableDevices.length > 0) {
      // Find the next available device that hasn't been used yet
      const usedDevices = formData.items
        .filter(item => item.sku === selectedProduct.sku)
        .map(item => item.serialNumber);
      
      const nextAvailableDevice = selectedProduct.availableDevices.find(
        device => !usedDevices.includes(device.serialNumber)
      ) || selectedProduct.availableDevices[0]; // Fallback to first if all used
      
      setCurrentItem({
        ...currentItem,
        productName: selectedProduct.name,
        sku: selectedProduct.sku,
        price: selectedProduct.price,
        serialNumber: nextAvailableDevice.serialNumber,
        customId: nextAvailableDevice.customId,
      });
    }
  };

  const handleProductClear = () => {
    console.log('Product cleared');
    setCurrentItem({
      ...currentItem,
      productName: '',
      sku: '',
      price: 0,
      serialNumber: '',
      customId: '',
    });
  };

  const addItem = () => {
    console.log('Add item clicked', currentItem);
    
    if (!currentItem.productName || !currentItem.sku) {
      setError('Please enter product name and SKU.');
      return;
    }
    
    // Always add as new item since each device has unique serial number and custom ID
    setFormData({
      ...formData,
      items: [...formData.items, currentItem],
    });
    
    // Reset current item
    setCurrentItem({
      productName: '',
      sku: '',
      quantity: 1,
      price: 0,
    });
    
    setError(null);
  };

  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index),
    });
  };

  const getTotalAmount = () => {
    return formData.items.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.name || !formData.recipientAddress1 || !formData.recipientCity || 
        !formData.recipientState || !formData.recipientZip || !formData.recipientPhone) {
      setError('Please fill in all required fields.');
      return;
    }

    // Validate that at least one item is added
    if (formData.items.length === 0) {
      setError('Please add at least one product to the order.');
      return;
    }

    // Pass the new order data to parent
    onSuccess(formData);
    
    // Reset form
    setFormData({
      name: '',
      orderNumber: '',
      orderDate: new Date().toLocaleDateString('en-GB'),
      recipientPhone: '',
      recipientAddress1: '',
      recipientAddress2: '',
      recipientCity: '',
      recipientState: '',
      recipientZip: '',
      recipientCountry: 'US',
      weight: '1.0',
      length: '10',
      width: '10',
      height: '10',
      notes: '',
      items: [],
    });
    
    setCurrentItem({
      productName: '',
      sku: '',
      quantity: 1,
      price: 0,
    });
    
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className={styles.addShipmentModal}>
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>Add New Shipment</Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <Form onSubmit={handleSubmit}>
          <h5 className={styles.sectionTitle}>Recipient Information</h5>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Recipient Name <span className={styles.required}>*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter recipient name"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone Number <span className={styles.required}>*</span></Form.Label>
                <Form.Control
                  type="tel"
                  name="recipientPhone"
                  value={formData.recipientPhone}
                  onChange={handleChange}
                  placeholder="e.g. 4045551234"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Address Line 1 <span className={styles.required}>*</span></Form.Label>
            <Form.Control
              type="text"
              name="recipientAddress1"
              value={formData.recipientAddress1}
              onChange={handleChange}
              placeholder="Street address"
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address Line 2 (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="recipientAddress2"
              value={formData.recipientAddress2}
              onChange={handleChange}
              placeholder="Apartment, suite, etc."
            />
          </Form.Group>

          <div style={{ display: 'flex', gap: '20px', marginBottom: '1rem' }}>
            <div style={{ flex: '1' }}>
              <Form.Group className="mb-3">
                <Form.Label>City <span className={styles.required}>*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="recipientCity"
                  value={formData.recipientCity}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </div>
            <div style={{ flex: '0 0 120px' }}>
              <Form.Group className="mb-3">
                <Form.Label>State <span className={styles.required}>*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="recipientState"
                  value={formData.recipientState}
                  onChange={handleChange}
                  maxLength={2}
                  placeholder="e.g. GA"
                  required
                />
              </Form.Group>
            </div>
            <div style={{ flex: '1' }}>
              <Form.Group className="mb-3">
                <Form.Label>Zip Code <span className={styles.required}>*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="recipientZip"
                  value={formData.recipientZip}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Country <span className={styles.required}>*</span></Form.Label>
            <Form.Select
              name="recipientCountry"
              value={formData.recipientCountry}
              onChange={handleChange}
              required
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="AU">Australia</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="JP">Japan</option>
            </Form.Select>
          </Form.Group>

          <h5 className={styles.sectionTitle}>Products <span className={styles.required}>*</span></h5>
          
          {error && (
            <Alert 
              variant="danger" 
              style={{ marginBottom: '1rem' }}
              dismissible
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}
          
          {/* Add Item Form */}
          <div style={{ display: 'flex', gap: '15px', marginBottom: '1rem', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
            <div className={styles.productNameField}>
              <FormItem
                label="Product Name *"
                className="customSelectWrapper"
                layout="vertical"
              >
                <Select
                  key="productName-select"
                  showSearch
                  allowClear
                  className="customSelect"
                  placeholder="Select a product"
                  value={currentItem.productName || undefined}
                  onChange={handleProductSelect}
                  onClear={handleProductClear}
                  style={{ width: '100%' }}
                  dropdownStyle={{ zIndex: 9999 }}
                >
                  {availableProducts.map(product => (
                    <Option key={product.sku} value={product.name}>
                      {product.name}
                    </Option>
                  ))}
                </Select>
              </FormItem>
            </div>
            <div className={styles.skuField}>
              <Form.Group className="mb-3">
                <Form.Label>SKU <span className={styles.required}>*</span></Form.Label>
                <Form.Control
                  type="text"
                  name="sku"
                  value={currentItem.sku}
                  onChange={handleItemChange}
                  placeholder="Auto-filled"
                  readOnly
                  style={{ backgroundColor: '#f5f5f5' }}
                />
              </Form.Group>
            </div>
            <Form.Group className={`mb-3 ${styles.qtyField}`}>
              <Form.Label>Qty</Form.Label>
              <Form.Control
                type="number"
                min="1"
                name="quantity"
                value={currentItem.quantity}
                onChange={handleItemChange}
                style={{ width: '70px', maxWidth: '70px', minWidth: '70px' }}
              />
            </Form.Group>
            <Form.Group className={`mb-3 ${styles.priceField}`}>
              <Form.Label>Price ($)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                name="price"
                value={currentItem.price}
                onChange={handleItemChange}
                style={{ width: '100px', maxWidth: '100px', minWidth: '100px' }}
              />
            </Form.Group>
            <div style={{ flex: '0 0 auto' }}>
              <Button
                type="button"
                onClick={addItem}
                className={styles.addItemButton}
              >
                +
              </Button>
            </div>
          </div>

          {/* Items List */}
          {formData.items.length > 0 && (
            <div className={styles.itemsList}>
              <table className={styles.itemsTable} style={{ 
                '--table-row-height': '40px',
                '--table-cell-padding': '4px 8px'
              } as React.CSSProperties}>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Serial #</th>
                    <th>Custom ID</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index} className={styles.productRow}>
                      <td>{item.productName}</td>
                      <td>{item.sku}</td>
                      <td>{item.serialNumber || '-'}</td>
                      <td>{item.customId || '-'}</td>
                      <td>{item.quantity}</td>
                      <td>${item.price.toFixed(2)}</td>
                      <td>${(item.price * item.quantity).toFixed(2)}</td>
                      <td>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className={styles.removeButton}
                        >
                          ×
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} className={styles.totalLabel}>Total Amount:</td>
                    <td className={styles.totalAmount}>${getTotalAmount()}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <h5 className={styles.sectionTitle}>Package Details</h5>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '1rem', flexWrap: 'nowrap' }}>
            <Form.Group className="mb-3" style={{ width: '110px', flex: '0 0 110px' }}>
              <Form.Label>Weight (lbs)</Form.Label>
              <Form.Control
                type="number"
                step="0.1"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                style={{ width: '110px', maxWidth: '110px', minWidth: '110px' }}
              />
            </Form.Group>
            <Form.Group className="mb-3" style={{ width: '110px', flex: '0 0 110px' }}>
              <Form.Label>Length (in)</Form.Label>
              <Form.Control
                type="number"
                name="length"
                value={formData.length}
                onChange={handleChange}
                style={{ width: '110px', maxWidth: '110px', minWidth: '110px' }}
              />
            </Form.Group>
            <Form.Group className="mb-3" style={{ width: '110px', flex: '0 0 110px' }}>
              <Form.Label>Width (in)</Form.Label>
              <Form.Control
                type="number"
                name="width"
                value={formData.width}
                onChange={handleChange}
                style={{ width: '110px', maxWidth: '110px', minWidth: '110px' }}
              />
            </Form.Group>
            <Form.Group className="mb-3" style={{ width: '110px', flex: '0 0 110px' }}>
              <Form.Label>Height (in)</Form.Label>
              <Form.Control
                type="number"
                name="height"
                value={formData.height}
                onChange={handleChange}
                style={{ width: '110px', maxWidth: '110px', minWidth: '110px' }}
              />
            </Form.Group>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Notes (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Add any special instructions or notes..."
            />
          </Form.Group>

          <div className={styles.modalFooter}>
            <Button variant="secondary" onClick={onHide} className={styles.cancelButton}>
              Cancel
            </Button>
            <Button type="submit" className={styles.submitButton}>
              Create Shipment
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddShipmentModal;
